import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { api, errMsg, fileUrl } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useCommunity } from "@/context/CommunityContext";
import TopBar from "@/components/TopBar";
import ImageUpload from "@/components/ImageUpload";
import UserPicker from "@/components/UserPicker";

export default function ChallengeDetail() {
  const { challengeId } = useParams();
  const { user } = useAuth();
  const { slug } = useCommunity();
  const [challenge, setChallenge] = useState(null);
  const [board, setBoard] = useState([]);
  const [form, setForm] = useState({ result_value: "", note: "", photo: null, video: null, code: "", witness_id: null });
  const [witnessName, setWitnessName] = useState("");
  const [error, setError] = useState("");
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!slug) return;
    api
      .get(`/communities/${slug}/challenges`)
      .then(({ data }) => setChallenge((data.challenges || []).find((c) => c.id === challengeId) || null))
      .catch(() => setChallenge(null));
  }, [slug, challengeId]);

  const loadBoard = () => {
    api.get(`/challenges/${challengeId}/leaderboard`).then(({ data }) => setBoard(data || [])).catch(() => setBoard([]));
  };
  useEffect(loadBoard, [challengeId]);

  const submit = async (event) => {
    event.preventDefault();
    setError("");
    const type = challenge?.challenge_type;
    if (["NUMBER_RESULT", "TIME_RESULT", "WITNESS_CONFIRM"].includes(type) && !String(form.result_value).trim()) {
      setError("Bitte trage dein Ergebnis ein.");
      return;
    }
    if (type === "WITNESS_CONFIRM" && !form.witness_id) {
      setError("Bitte wähle eine Person, die dein Ergebnis bestätigt.");
      return;
    }
    setBusy(true);
    try {
      const { data } = await api.post(`/challenges/${challengeId}/entries`, form);
      setResult(data.entry);
      loadBoard();
    } catch (e) {
      setError(errMsg(e));
    } finally {
      setBusy(false);
    }
  };

  if (!challenge) return <div className="p-10 text-center text-sm text-[var(--ink-soft)]">wird geladen …</div>;
  const type = challenge.challenge_type;

  return (
    <div data-testid="challenge-detail-page">
      <TopBar back="/challenges" title={challenge.title} />
      {challenge.image ? (
        <div className="h-44 w-full bg-[var(--brand-soft)]">
          <img src={fileUrl(challenge.image)} alt="" className="h-full w-full object-cover" />
        </div>
      ) : null}
      <div className="mx-auto max-w-2xl px-5 py-7">
        <h1 className="text-4xl">{challenge.title}</h1>
        {challenge.short_description ? <p className="mt-2 text-base text-[var(--ink-soft)]">{challenge.short_description}</p> : null}
        {challenge.description ? <p className="mt-4 whitespace-pre-line text-base leading-relaxed">{challenge.description}</p> : null}
        {challenge.instruction ? (
          <p className="mt-4 rounded-xl bg-[var(--brand-soft)] px-4 py-3 text-sm">{challenge.instruction}</p>
        ) : null}
        {challenge.rules_text ? <p className="mt-4 whitespace-pre-line text-sm text-[var(--ink-soft)]">{challenge.rules_text}</p> : null}

        {!user ? (
          <div className="mt-7 rounded-2xl border bg-white p-5">
            <p className="text-sm">Für Challenges brauchst du ein Konto.</p>
            <Link to="/login" className="btn btn-primary mt-3 !py-2.5 !text-xs" data-testid="challenge-login">ANMELDEN</Link>
          </div>
        ) : result ? (
          <div className="mt-7 rounded-2xl border bg-white p-5" data-testid="challenge-result">
            <p className="text-2xl">
              {result.status === "CONFIRMED" ? "Gespeichert und bestätigt." : "Bestätigung angefragt."}
            </p>
            <p className="mt-2 text-sm text-[var(--ink-soft)]">
              {result.status === "CONFIRMED"
                ? `${result.result_value || "Erledigt"} ${result.unit_label || ""}`.trim()
                : `${witnessName || "Die gewählte Person"} muss dein Ergebnis noch bestätigen.`}
            </p>
            <button className="btn btn-ghost mt-4 !py-2.5 !text-xs" onClick={() => { setResult(null); setForm({ result_value: "", note: "", photo: null, video: null, code: "", witness_id: null }); }} data-testid="challenge-again">
              NOCH EIN VERSUCH
            </button>
          </div>
        ) : (
          <form onSubmit={submit} className="mt-7 space-y-4 rounded-2xl border bg-white p-5" data-testid="challenge-form">
            {["NUMBER_RESULT", "TIME_RESULT", "WITNESS_CONFIRM"].includes(type) ? (
              <div>
                <div className="eyebrow mb-1">
                  {type === "TIME_RESULT" ? "Deine Zeit" : `Wie viele hast du geschafft?${challenge.unit_label ? ` (${challenge.unit_label})` : ""}`}
                </div>
                <input
                  className="field"
                  value={form.result_value}
                  onChange={(e) => setForm({ ...form, result_value: e.target.value })}
                  data-testid="challenge-result-value"
                />
              </div>
            ) : null}
            {type === "CODE_CONFIRM" ? (
              <div>
                <div className="eyebrow mb-1">Code</div>
                <input className="field" value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value })} data-testid="challenge-code" />
              </div>
            ) : null}
            {type === "PHOTO_UPLOAD" ? (
              <ImageUpload value={form.photo} onChange={(v) => setForm({ ...form, photo: v })} label="Foto" testId="challenge-photo" />
            ) : null}
            {type === "VIDEO_UPLOAD" ? (
              <ImageUpload value={form.video} onChange={(v) => setForm({ ...form, video: v })} label="Video" testId="challenge-video" accept="video/*" />
            ) : null}
            {type === "WITNESS_CONFIRM" ? (
              <UserPicker
                value={form.witness_id}
                onChange={(id, name) => {
                  setForm({ ...form, witness_id: id });
                  setWitnessName(name);
                }}
                label="Zeuge auswählen"
                testId="challenge-witness"
              />
            ) : null}
            <div>
              <div className="eyebrow mb-1">Notiz (optional)</div>
              <textarea className="field min-h-[80px]" value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} data-testid="challenge-note" />
            </div>
            {error ? <p className="text-sm text-red-700" data-testid="challenge-error">{error}</p> : null}
            <button className="btn btn-primary w-full" disabled={busy} data-testid="challenge-submit">
              {type === "WITNESS_CONFIRM" ? "BESTÄTIGUNG ANFRAGEN" : "ERGEBNIS SPEICHERN"}
            </button>
          </form>
        )}

        {challenge.ranking_enabled ? (
          <section className="mt-9" data-testid="challenge-leaderboard">
            <p className="eyebrow">Bestleistungen</p>
            {board.length === 0 ? (
              <p className="mt-3 text-sm text-[var(--ink-soft)]">Noch keine bestätigten Ergebnisse.</p>
            ) : (
              <ol className="mt-3 divide-y overflow-hidden rounded-2xl border bg-white">
                {board.map((row, i) => (
                  <li key={row.user_id} className="flex items-center gap-3 px-4 py-3 text-sm">
                    <span className="w-5 text-[var(--ink-soft)]">{i + 1}</span>
                    <span className="flex-1 truncate">{row.user_name}</span>
                    <span className="font-semibold">{row.result_value} {row.unit_label || ""}</span>
                  </li>
                ))}
              </ol>
            )}
          </section>
        ) : null}
      </div>
    </div>
  );
}
